import React, { useEffect, useMemo, useRef, useState } from "react";
import MaterialReactTable, {
  MRT_ColumnFiltersState,
} from "material-react-table";
import { useMutation } from "@tanstack/react-query";
import { Box, Button, Dialog } from "@mui/material";
import dayjs from "dayjs";

import {
  AdaptedCollectorsMissingDocuments,
  ConfigParameterDTO,
  postMissingDocuments,
} from "api";

import {
  BulkActionsToolbar,
  COLORS,
  EsgSingleSelectControlledField,
  Loading,
  MultiSelectControlledOption,
  Pagination,
} from "components";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";

import {
  useGetAllowedValues,
  useGetConfigParameter,
  usePagination,
  useSites,
  useUtilityBillsYears,
  useCustomRowSelection,
} from "hooks";
import { createCustomRowSelectionColumn } from "components/table/customColumns";

import {
  calculateCurrentFiscalYear,
  getFiscalYear,
  safeParseJSON,
} from "utils";
import { ALL_MONTHS, LOCAL_STORAGE_KEYS } from "consts";

import {
  BulkActionsButtons,
  CollectorMonthView,
  DataBoxes,
  MonthCell,
} from "./components";
import {
  DATA_BOXES,
  ALL_STATUS_OPTIONS,
  MISSING_DOCUMENTS_STATIC_COLUMNS,
  INITIAL_STATUS_OPTIONS,
} from "./consts";
import { completeMonths, getStatusOptions } from "./helpers";
import { DefaultObjectProps } from "./types";

export const MissingDocumentsPage = () => {
  const { data: configParameter } = useGetConfigParameter();
  const { data: availableYears } = useUtilityBillsYears();
  const { data: allowedValues } = useGetAllowedValues();
  const { data: sitesData } = useSites();
  const dataBoxes = [...DATA_BOXES];
  const allMonths = useMemo(() => [...ALL_MONTHS], []);

  // Local Storage
  const storedFiscalYear = localStorage.getItem(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_FISCAL_YEAR,
  );
  const storedCountry = localStorage.getItem(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY,
  );
  const storedUtilityType = localStorage.getItem(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_UTILITY_TYPE,
  );
  const storedBusinessUnit = localStorage.getItem(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_BUSINESS_UNIT,
  );
  const storedCountryRegion = localStorage.getItem(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY_REGION,
  );
  const storedSiteIds = localStorage.getItem(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_SITE_IDS,
  );
  const storedStatus = localStorage.getItem(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_STATUS,
  );

  const sessionFilterValue = localStorage.getItem(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COLUMN_FILTERS,
  );

  let storedfilterValues: MRT_ColumnFiltersState = [];
  if (Array.isArray(sessionFilterValue)) {
    storedfilterValues = sessionFilterValue as MRT_ColumnFiltersState;
  } else if (typeof sessionFilterValue === "string") {
    storedfilterValues = safeParseJSON<MRT_ColumnFiltersState>(
      sessionFilterValue,
      [],
    );
  }

  const {
    pagination,
    paginatedQuery,
    onPageChange,
    onSizeChange,
    updateFromApiResponse,
    resetPage,
    uiPage,
  } = usePagination({
    localStorageKey: LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_PAGINATION,
    initialPage: 0,
    initialSize: 50,
  });

  const {
    mutateAsync: postMissingDocumentsRequest,
    isLoading: loadingMissingDocuments,
  } = useMutation(postMissingDocuments);

  const companyMonths = useMemo(
    () =>
      configParameter
        ?.filter((param: ConfigParameterDTO) =>
          param.name.includes("company_month"),
        )
        .map((param: ConfigParameterDTO) => param.configValue) ?? [
        "Jan",
        "Dec",
      ],
    [configParameter],
  );

  const allFiscalYears: string[] = useMemo(
    () =>
      (availableYears &&
        availableYears.map((fy: number) => `${fy} Fiscal Year`)) || [
        `${dayjs().get("year")} Fiscal Year`,
      ],
    [availableYears],
  );

  const countries = useMemo(
    () =>
      allowedValues
        ? allowedValues.countries.sort().map((coun: string) => ({
          name: coun.replaceAll("_", " "),
          label: coun,
          value: coun,
        }))
        : [],
    [allowedValues],
  );

  const utilityTypes = useMemo(
    () =>
      allowedValues
        ? allowedValues.utilityTypes
          .sort((utA: any, utB: any) => (utA.name > utB.name ? 1 : -1))
          .map((ut: any) => ({ name: ut.name, label: ut.name, value: ut.id }))
        : [],
    [allowedValues],
  );

  const sites = useMemo(
    () =>
      sitesData
        ? sitesData.map((site: any) => ({
          name: site.name,
          label: site.name,
          value: site.id,
        }))
        : [],
    [sitesData],
  );

  const businessUnits = useMemo(
    () =>
      allowedValues
        ? allowedValues.businessUnits
          .sort()
          .map((bu: string) => ({ name: bu, label: bu, value: bu }))
        : [],
    [allowedValues],
  );

  const countryRegions = useMemo(
    () =>
      allowedValues
        ? allowedValues.countryRegions
          .sort()
          .map((cr: string) => ({ name: cr, label: cr, value: cr }))
        : [],
    [allowedValues],
  );

  const sitesByCountry = useMemo(
    () =>
      sitesData
        ? sitesData.reduce((acc: any, site: any) => {
          if (!acc[site.country]) {
            acc[site.country] = [];
          }
          acc[site.country].push(site.id);
          return acc;
        }, [])
        : [],
    [sitesData],
  );

  const [currentMissingData, setCurrentMissingData] = useState<any[]>([]);
  const [allCurrentCollectorsIds, setAllCurrentCollectorsIds] = useState<
    string[]
  >([]);
  const initialFY =
    storedFiscalYear ?? calculateCurrentFiscalYear(companyMonths);
  const [currentFY, setCurrentFY] = useState<string>(initialFY);
  const [monthHeaderFY, setMonthHeaderFY] = useState<string>(
    currentFY?.substring(0, 4) || "",
  );
  const [isOnlyMissingButtonActive, setIsOnlyMissingButtonActive] =
    useState<boolean>(false);

  const [currentCountries, setCurrentCountries] = useState<
    MultiSelectControlledOption[]
  >(() =>
    storedCountry
      ? safeParseJSON<MultiSelectControlledOption[]>(storedCountry, [])
      : [],
  );
  const [currentSiteIds, setCurrentSiteIds] = useState<
    MultiSelectControlledOption[]
  >(() =>
    storedSiteIds
      ? safeParseJSON<MultiSelectControlledOption[]>(storedSiteIds, [])
      : [],
  );
  const [currentUtilityTypeIds, setCurrentUtilityTypeIds] = useState<
    MultiSelectControlledOption[]
  >(() =>
    storedUtilityType
      ? safeParseJSON<MultiSelectControlledOption[]>(storedUtilityType, [])
      : [],
  );
  const [currentBusinessUnits, setCurrentBusinessUnits] = useState<
    MultiSelectControlledOption[]
  >(() =>
    storedBusinessUnit
      ? safeParseJSON<MultiSelectControlledOption[]>(storedBusinessUnit, [])
      : [],
  );
  const [currentCountryRegions, setCurrentCountryRegions] = useState<
    MultiSelectControlledOption[]
  >(() =>
    storedCountryRegion
      ? safeParseJSON<MultiSelectControlledOption[]>(storedCountryRegion, [])
      : [],
  );
  const [currentStatus, setCurrentStatus] = useState<
    MultiSelectControlledOption[]
  >(() => {
    const parsed = storedStatus
      ? safeParseJSON<MultiSelectControlledOption[]>(storedStatus, [])
      : [];
    return Array.isArray(parsed) && parsed.length > 0
      ? parsed
      : getStatusOptions(INITIAL_STATUS_OPTIONS);
  });

  const [columnFilters, setColumnFilters] =
    useState<MRT_ColumnFiltersState>(storedfilterValues);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [searchActive, setSearchActive] = useState<boolean>(false);
  const [enableSearch, setEnableSeach] = useState<boolean>(false);
  const [isSitesFiltered, setIsSitesFiltered] = useState<boolean>(false);
  const [isTableRequestInProgress, setIsTableRequestInProgress] =
    useState<boolean>(false);
  const [selectedMonthCell, setSelectedMonthCell] = useState<{
    month: string;
    year: number;
  }>({ month: "", year: Number(currentFY?.substring(0, 4)) });
  const [collectorMissingDocuments, setCollectorMissingDocuments] =
    useState<AdaptedCollectorsMissingDocuments>();
  const [openMonthView, setOpenMonthView] = useState<boolean>(false);
  const selectedfiscalYear = getFiscalYear(companyMonths, currentFY);

  const defaultObject = useRef<DefaultObjectProps>({
    siteIds: sites.map((site: any) => site.value),
    utilityTypeIds: null,
    fromDate: selectedfiscalYear.fromDate,
    toDate: selectedfiscalYear.endDate,
    businessUnits: null,
    countryRegions: null,
    onlyMissing: false,
    page: 0,
    size: 50,
    collectorStatuses: INITIAL_STATUS_OPTIONS.map((s) => s.key),
  });

  const { size, page, totalRows } = pagination;

  // Custom row selection hook
  const {
    selectedRows,
    allRowsSelected,
    totalSelectedRows,
    handleRowToggle,
    handleSelectAll,
    resetSelection,
  } = useCustomRowSelection({
    totalRows,
    data: currentMissingData,
  });

  const monthHeaders = useMemo(
    () => completeMonths(companyMonths, Number(monthHeaderFY)),
    [companyMonths, monthHeaderFY],
  );

  const monthColumns = useMemo(() => monthHeaders.map(
    ({ header: stringMonthValue, year: monthYear }) => {
      const monthKey = stringMonthValue.substring(0, 3);
      const numberMonthValue = allMonths.indexOf(monthKey) + 1;

      return {
        id: `${monthHeaderFY}-${numberMonthValue}`,
        header: stringMonthValue,
        accessorFn: (row: any) => row.months?.[monthKey],
        Cell: ({ cell, row }: { cell: any; row: any }) => {
          const { collector, months, unit } = row.original;
          const handleMonthSelectedOpen = () => {
            setSelectedMonthCell({
              month: monthKey,
              year: monthYear,
            });
            setCollectorMissingDocuments({
              unit,
              id: collector.id,
              collector,
              days: allMonths.reduce((acc: any, month: string) => {
                const monthIndex = allMonths.indexOf(month) + 1;
                if (!acc[monthIndex]) {
                  acc[monthIndex] = months[month]
                    ? months[month].days.reduce(
                      (dayAcc: any, currentDay: any) => ({
                        ...dayAcc,
                        [currentDay.day]: {
                          usage: currentDay.usage ?? null,
                          dayBillType: currentDay.dayBillType ?? null,
                        },
                      }),
                      {},
                    )
                    : null;
                }
                return acc;
              }, {}),
              months: allMonths.reduce((acc: any, month: string) => {
                if (!acc[allMonths.indexOf(month) + 1]) {
                  acc[allMonths.indexOf(month) + 1] = {
                    usage: months[month]?.monthUsage ?? null,
                    monthBillType: months[month]?.monthBillType ?? null,
                  };
                }
                return acc;
              }, {}),
            });
            setOpenMonthView(true);
          };

          return (
            <MonthCell
              cell={cell}
              monthYear={monthYear}
              numberMonthValue={numberMonthValue}
              isMonthModalOpen={openMonthView}
              handleMonthSelectedOpen={handleMonthSelectedOpen}
            />
          );
        },
        enableSorting: false,
        minSize: 120,
        size: 120,
      };
    },
  ), [monthHeaders, monthHeaderFY, openMonthView, allMonths]);

  // Custom row selection column
  const customRowSelectionColumn = createCustomRowSelectionColumn({
    selectedRows,
    allRowsSelected,
    totalRows,
    totalSelectedRows,
    tooltip:
      "Select All selects all collectors across all pages for the current filter.",
    onRowToggle: handleRowToggle,
    onSelectAll: handleSelectAll,
  });

  const MISSING_DOCUMENTS_COLUMNS = [
    ...customRowSelectionColumn,
    ...MISSING_DOCUMENTS_STATIC_COLUMNS,
    ...monthColumns,
  ];

  const handleUpdateMissingDocuments = async (payloadFromPagination?: any) => {
    setIsTableRequestInProgress(true);
    const missingDocRequestBody = payloadFromPagination ?? {
      ...defaultObject.current,
      ...paginatedQuery,
    };

    try {
      await postMissingDocumentsRequest(missingDocRequestBody, {
        onSuccess: (response: any) => {
          const { data } = response;
          const responseArray = Array.isArray(data?.response)
            ? data.response
            : Array.isArray((data as any)?.content)
              ? (data as any).content
              : [];
          updateFromApiResponse(data?.pageResult);
          const parsedData = responseArray.reduce(
            (acc: any, currentCol: any, index: number) => {
              const { collector, unit, days } = currentCol;
              acc[index] = {
                id: collector.id,
                collector,
                unit,
                months: (() => {
                  // Group days by month name in a single O(n) pass 
                  const byMonth: Record<string, any[]> = days.reduce(
                    (monthGroup: Record<string, any[]>, d: any) => {
                      const monthName = allMonths[parseInt(d.day.substring(5, 7), 10) - 1];
                      return {
                        ...monthGroup,
                        [monthName]: [...(monthGroup[monthName] ?? []), d],
                      };
                    },
                    {},
                  );

                  return Object.entries(byMonth).reduce(
                    (monthAcc: any, [monthName, monthDays]) => {
                      const uniqueBillTypes = Array.from(
                        new Set(monthDays.map((d: any) => d.billType).filter(Boolean)),
                      );
                      const hasActual = uniqueBillTypes.includes("ACTUAL");
                      const hasInterpolationOrExtrapolation =
                        uniqueBillTypes.includes("INTERPOLATION") ||
                        uniqueBillTypes.includes("EXTRAPOLATION");

                      return {
                        ...monthAcc,
                        [monthName]: {
                          days: monthDays.map(
                            ({
                              day,
                              usage,
                              billType,
                            }: {
                              day: string;
                              usage: number;
                              billType: string;
                            }) => ({
                              day: day.substring(8),
                              usage,
                              dayBillType: billType,
                            }),
                          ),
                          monthBillType:
                            uniqueBillTypes.length > 1 &&
                              hasActual &&
                              hasInterpolationOrExtrapolation
                              ? "MIXED"
                              : uniqueBillTypes[0] ?? null,
                          monthUsage: monthDays.reduce(
                            (usageAcc: number, d: any) => usageAcc + (d.usage ?? 0),
                            0,
                          ),
                        },
                      };
                    },
                    {},
                  );
                })(),
                status: collector?.collectorStatus ?? "",
              };
              return acc;
            },
            [],
          );
          setMonthHeaderFY(currentFY?.substring(0, 4) || "");
          setCurrentMissingData(parsedData);
          setAllCurrentCollectorsIds(data?.allCollectorIds ?? []);
        },
        onError: () => {
          // implement error handling
        },
      });
      resetSelection();
      setSearchActive(false);
    } finally {
      setIsTableRequestInProgress(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    onPageChange(newPage);
    handleUpdateMissingDocuments({
      ...defaultObject.current,
      page: newPage - 1,
      size,
    });
  };

  const handleSizeChange = (newSize: number) => {
    onSizeChange(newSize);
    handleUpdateMissingDocuments({
      ...defaultObject.current,
      page: 0,
      size: newSize,
    });
  };

  const onOnlyMissingButtonClick = () => {
    setIsOnlyMissingButtonActive(true);
    defaultObject.current = {
      ...defaultObject.current,
      onlyMissing: true,
    };
    handleUpdateMissingDocuments();
  };

  const onShowAllButtonClick = () => {
    setIsOnlyMissingButtonActive(false);
    defaultObject.current = {
      ...defaultObject.current,
      onlyMissing: false,
    };
    handleUpdateMissingDocuments();
  };

  const handleClearFilters = () => {
    const resetToCurrentFiscalYear = calculateCurrentFiscalYear(companyMonths);

    setCurrentFY(resetToCurrentFiscalYear);
    setCurrentCountries([]);
    setCurrentSiteIds([]);
    setCurrentUtilityTypeIds([]);
    setCurrentBusinessUnits([]);
    setCurrentCountryRegions([]);
    setCurrentStatus(getStatusOptions(INITIAL_STATUS_OPTIONS));
    setColumnFilters([]);
    setIsSitesFiltered(false);
    setIsOnlyMissingButtonActive(false);
    resetSelection();
    resetPage();
    defaultObject.current = {
      ...defaultObject.current,
      siteIds: sites.map((site: any) => site.value),
      utilityTypeIds: null,
      fromDate: getFiscalYear(companyMonths, resetToCurrentFiscalYear).fromDate,
      toDate: getFiscalYear(companyMonths, resetToCurrentFiscalYear).endDate,
      businessUnits: null,
      countryRegions: null,
      onlyMissing: false,
      collectorStatuses: INITIAL_STATUS_OPTIONS.map((s) => s.key),
    };

    localStorage.setItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_FISCAL_YEAR,
      resetToCurrentFiscalYear,
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY,
      "[]",
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_UTILITY_TYPE,
      "[]",
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_SITE_IDS,
      "[]",
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_BUSINESS_UNIT,
      "[]",
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY_REGION,
      "[]",
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_STATUS,
      JSON.stringify(getStatusOptions(INITIAL_STATUS_OPTIONS)),
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COLUMN_FILTERS,
      "[]",
    );

    handleUpdateMissingDocuments({
      ...defaultObject.current,
      page: 0,
      size,
    });
  };

  const updateArr = (
    arr: any[],
    value: string | number,
    unchecked?: boolean,
    isCountrySelection: boolean = false,
  ) => {
    const safeArr = Array.isArray(arr) ? arr : [];
    const currentSelection = Array.from(
      new Set([...safeArr.map((item: any) => item.value), value]),
    );
    if (isCountrySelection) {
      setIsSitesFiltered(true);
      if (unchecked) {
        const filteredSelection = safeArr
          .filter((item: any) => item.value !== value)
          .map((item: any) => item.value);
        if (!filteredSelection.length) {
          setIsSitesFiltered(false);
          return null;
        }
        return filteredSelection;
      }
      if (!(currentSelection.includes(value) && !unchecked)) {
        const countriesSelected = currentSelection.filter(
          (country: any) => country !== value,
        );
        const idsByCountriesSelected = countriesSelected
          .map((country: any) => sitesByCountry[country])
          .flat()
          .filter((site: any) => site !== undefined);
        return idsByCountriesSelected;
      }
      const idsByCountriesSelected = currentSelection
        .map((country: any) => sitesByCountry[country])
        .flat()
        .filter((site: any) => site !== undefined);
      return idsByCountriesSelected;
    }
    if (safeArr.find((item: any) => item.value === "ALL")) {
      if (isCountrySelection) setIsSitesFiltered(false);
      return null;
    }
    if (unchecked) {
      const filteredSelection = safeArr
        .filter((item: any) => item.value !== value)
        .map((item: any) => item.value);
      if (!filteredSelection.length) return null;
      return filteredSelection;
    }
    return currentSelection.includes("ALL") ? null : currentSelection;
  };

  const getSitesFiltered = () =>
    sitesData
      ?.filter((site: any) =>
        (Array.isArray(currentCountries) ? currentCountries : [])
          .map((country: any) => country.value)
          .flat()
          .includes(site.country),
      )
      .map((site: any) => ({
        name: site.name,
        label: site.name,
        value: site.id,
      })) ?? [];

  const createSetSelectedValues =
    (
      storageKey: string,
      setState: (param: MultiSelectControlledOption[]) => void,
      getDefaultObjectUpdate: (
        param: MultiSelectControlledOption[],
      ) => Partial<DefaultObjectProps>,
      options?: { setSearchActive?: boolean },
    ) =>
      (param: MultiSelectControlledOption[]) => {
        setState(param);
        localStorage.setItem(storageKey, JSON.stringify(param));
        const update = getDefaultObjectUpdate(param);
        if (Object.keys(update).length > 0) {
          defaultObject.current = {
            ...defaultObject.current,
            ...update,
          };
        }
        if (options?.setSearchActive !== false) setSearchActive(true);
      };

  const createOnChangeFn =
    (config: {
      storageKey: string;
      setState: (param: MultiSelectControlledOption[]) => void;
      run: (
        value: string | number,
        unchecked: boolean | undefined,
      ) => {
        newParam: MultiSelectControlledOption[];
        defaultObjectUpdate: Partial<DefaultObjectProps>;
      };
      updateStateAndStorage?: boolean;
    }) =>
      (value: string | number, unchecked: boolean | undefined) => {
        const { newParam, defaultObjectUpdate } = config.run(value, unchecked);
        defaultObject.current = {
          ...defaultObject.current,
          ...defaultObjectUpdate,
        };
        if (config.updateStateAndStorage !== false) {
          config.setState(newParam);
          localStorage.setItem(config.storageKey, JSON.stringify(newParam));
        }
        setSearchActive(true);
      };

  useEffect(() => {
    const hasStoredState =
      storedFiscalYear ||
      storedSiteIds ||
      storedCountry ||
      storedUtilityType ||
      storedBusinessUnit ||
      storedCountryRegion ||
      storedStatus;
    if (hasStoredState) {
      setEnableSeach(true);
      return;
    }

    defaultObject.current = {
      ...defaultObject.current,
      siteIds: sites.map((site: any) => site.value),
      utilityTypeIds: null,
      fromDate: getFiscalYear(companyMonths, currentFY || "").fromDate,
      toDate: getFiscalYear(companyMonths, currentFY || "").endDate,
      businessUnits: null,
      countryRegions: null,
    };
    setEnableSeach(true);
  }, [sites]); // eslint-disable-line

  useEffect(() => {
    if (!storedFiscalYear && companyMonths) {
      const fy = calculateCurrentFiscalYear(companyMonths);
      if (currentFY !== fy) {
        setCurrentFY(fy);
        setMonthHeaderFY(fy.substring(0, 4));
      }
    }
  }, [companyMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COLUMN_FILTERS,
      JSON.stringify(columnFilters),
    );
  }, [columnFilters]);

  // Restore selector state from localStorage when the page is entered (e.g. when navigating back)
  useEffect(() => {
    const fy = localStorage.getItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_FISCAL_YEAR,
    );
    const country = localStorage.getItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY,
    );
    const siteIdsRaw = localStorage.getItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_SITE_IDS,
    );
    const utilityType = localStorage.getItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_UTILITY_TYPE,
    );
    const businessUnit = localStorage.getItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_BUSINESS_UNIT,
    );
    const countryRegion = localStorage.getItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY_REGION,
    );
    const status = localStorage.getItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_STATUS,
    );

    const parsedCountry =
      country != null
        ? safeParseJSON<MultiSelectControlledOption[]>(country, [])
        : [];
    const parsedSiteIds =
      siteIdsRaw != null
        ? safeParseJSON<MultiSelectControlledOption[]>(siteIdsRaw, [])
        : [];
    const parsedUtilityType =
      utilityType != null
        ? safeParseJSON<MultiSelectControlledOption[]>(utilityType, [])
        : [];
    const parsedBusinessUnit =
      businessUnit != null
        ? safeParseJSON<MultiSelectControlledOption[]>(businessUnit, [])
        : [];
    const parsedCountryRegion =
      countryRegion != null
        ? safeParseJSON<MultiSelectControlledOption[]>(countryRegion, [])
        : [];
    const parsedStatus = status
      ? safeParseJSON<MultiSelectControlledOption[]>(status, [])
      : [];

    if (fy) {
      setCurrentFY(fy);
      setMonthHeaderFY(fy.substring(0, 4));
    }
    if (country != null) setCurrentCountries(parsedCountry);
    if (siteIdsRaw != null) setCurrentSiteIds(parsedSiteIds);
    if (utilityType != null) setCurrentUtilityTypeIds(parsedUtilityType);
    if (businessUnit != null) setCurrentBusinessUnits(parsedBusinessUnit);
    if (countryRegion != null) setCurrentCountryRegions(parsedCountryRegion);
    if (status) {
      setCurrentStatus(
        parsedStatus.length > 0
          ? parsedStatus
          : getStatusOptions(INITIAL_STATUS_OPTIONS),
      );
    }

    // Sync defaultObject so the API request uses the restored filters (not defaults)
    const fiscalYearToUse = fy ?? initialFY;
    const { fromDate, endDate } = getFiscalYear(companyMonths, fiscalYearToUse);
    const siteIdsForApi =
      parsedSiteIds.length > 0
        ? parsedSiteIds.map((o: MultiSelectControlledOption) => o.value)
        : sites.map((site: any) => site.value);
    defaultObject.current = {
      ...defaultObject.current,
      siteIds: siteIdsForApi,
      utilityTypeIds:
        parsedUtilityType.length > 0
          ? parsedUtilityType.map((o: MultiSelectControlledOption) => o.value)
          : null,
      fromDate,
      toDate: endDate,
      businessUnits:
        parsedBusinessUnit.length > 0
          ? parsedBusinessUnit.map((o: MultiSelectControlledOption) => o.value)
          : null,
      countryRegions:
        parsedCountryRegion.length > 0
          ? parsedCountryRegion.map((o: MultiSelectControlledOption) => o.value)
          : null,
      onlyMissing: false,
      page: 0,
      size: 50,
      collectorStatuses:
        parsedStatus.length > 0
          ? parsedStatus.map((o: MultiSelectControlledOption) => o.value)
          : INITIAL_STATUS_OPTIONS.map((s) => s.key),
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleUpdateMissingDocuments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return !availableYears ? (
    <Loading />
  ) : (
    <Box sx={{ margin: "12px 24px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <EsgSingleSelectControlledField
          label="Fiscal Year"
          placeholder="Select Fiscal Year"
          options={allFiscalYears.map((year) => ({
            label: year,
            value: year,
          }))}
          selectedValue={currentFY}
          setSelectedValue={(value: string | number) => {
            const fyValue = value as string;
            setCurrentFY(fyValue);
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_FISCAL_YEAR,
              fyValue,
            );
            defaultObject.current = {
              ...defaultObject.current,
              fromDate: getFiscalYear(companyMonths, fyValue.substring(0, 4))
                .fromDate,
              toDate: getFiscalYear(companyMonths, fyValue.substring(0, 4))
                .endDate,
            };
            setSearchActive(true);
          }}
        />
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Country"
            selectedValues={currentCountries}
            setSelectedValues={createSetSelectedValues(
              LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY,
              setCurrentCountries,
              (param) => ({
                siteIds:
                  param.length > 0
                    ? (param.flatMap(
                      (c) => sitesByCountry[c.value] ?? [],
                    ) as number[])
                    : sites.map((site: any) => site.value),
              }),
            )}
            options={countries}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={createOnChangeFn({
              storageKey: LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY,
              setState: setCurrentCountries,
              run: (value, unchecked) => {
                const newSiteIds =
                  updateArr(currentCountries, value, unchecked, true) ??
                  sites.map((site: any) => site.value);
                const newParam = unchecked
                  ? currentCountries.filter((c: any) => c.value !== value)
                  : ([
                    ...currentCountries,
                    countries.find((c) => c.value === value) ?? {
                      value,
                      label: String(value),
                      name: String(value),
                    },
                  ].filter(Boolean) as MultiSelectControlledOption[]);
                return {
                  newParam,
                  defaultObjectUpdate: { siteIds: newSiteIds },
                };
              },
            })}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Utility Type"
            selectedValues={currentUtilityTypeIds}
            setSelectedValues={createSetSelectedValues(
              LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_UTILITY_TYPE,
              setCurrentUtilityTypeIds,
              (param) => ({
                utilityTypeIds:
                  param.length > 0 ? param.map((o) => o.value) : null,
              }),
            )}
            options={utilityTypes}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={createOnChangeFn({
              storageKey:
                LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_UTILITY_TYPE,
              setState: setCurrentUtilityTypeIds,
              run: (value, unchecked) => {
                const newVal = updateArr(
                  currentUtilityTypeIds,
                  value,
                  unchecked,
                );
                const newParam = (
                  newVal != null
                    ? newVal.map(
                      (v) =>
                        utilityTypes.find((o) => o.value === v) ?? {
                          value: v,
                          label: String(v),
                          name: String(v),
                        },
                    )
                    : []
                ) as MultiSelectControlledOption[];
                return {
                  newParam,
                  defaultObjectUpdate: { utilityTypeIds: newVal },
                };
              },
            })}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Sites"
            selectedValues={currentSiteIds}
            setSelectedValues={createSetSelectedValues(
              LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_SITE_IDS,
              setCurrentSiteIds,
              (param) => ({
                siteIds:
                  param.length > 0
                    ? param.map((o) => o.value)
                    : sites.map((site: any) => site.value),
              }),
            )}
            options={!isSitesFiltered ? sites : getSitesFiltered()}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={createOnChangeFn({
              storageKey: LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_SITE_IDS,
              setState: setCurrentSiteIds,
              run: (value, unchecked) => {
                const newIds =
                  updateArr(currentSiteIds, value, unchecked) ??
                  sites.map((site: any) => site.value);
                const opts = !isSitesFiltered ? sites : getSitesFiltered();
                const newParam = (Array.isArray(newIds) ? newIds : []).map(
                  (id) =>
                    opts.find((s: any) => s.value === id) ?? {
                      value: id,
                      label: String(id),
                      name: String(id),
                    },
                ) as MultiSelectControlledOption[];
                return {
                  newParam,
                  defaultObjectUpdate: { siteIds: newIds },
                };
              },
            })}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Business Unit"
            selectedValues={currentBusinessUnits}
            setSelectedValues={createSetSelectedValues(
              LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_BUSINESS_UNIT,
              setCurrentBusinessUnits,
              (param) => ({
                businessUnits:
                  param.length > 0 ? param.map((o) => o.value) : null,
              }),
            )}
            options={businessUnits}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={createOnChangeFn({
              storageKey:
                LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_BUSINESS_UNIT,
              setState: setCurrentBusinessUnits,
              run: (value, unchecked) => {
                const newVal = updateArr(
                  currentBusinessUnits,
                  value,
                  unchecked,
                );
                const newParam = (
                  newVal != null
                    ? newVal.map(
                      (v) =>
                        businessUnits.find((o) => o.value === v) ?? {
                          value: v,
                          label: String(v),
                          name: String(v),
                        },
                    )
                    : []
                ) as MultiSelectControlledOption[];
                return {
                  newParam,
                  defaultObjectUpdate: { businessUnits: newVal },
                };
              },
            })}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Region"
            selectedValues={currentCountryRegions}
            setSelectedValues={createSetSelectedValues(
              LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY_REGION,
              setCurrentCountryRegions,
              (param) => ({
                countryRegions:
                  param.length > 0 ? param.map((o) => o.value) : null,
              }),
            )}
            options={countryRegions}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={createOnChangeFn({
              storageKey:
                LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY_REGION,
              setState: setCurrentCountryRegions,
              run: (value, unchecked) => {
                const newVal = updateArr(
                  currentCountryRegions,
                  value,
                  unchecked,
                );
                const newParam = (
                  newVal != null
                    ? newVal.map(
                      (v) =>
                        countryRegions.find((o) => o.value === v) ?? {
                          value: v,
                          label: String(v),
                          name: String(v),
                        },
                    )
                    : []
                ) as MultiSelectControlledOption[];
                return {
                  newParam,
                  defaultObjectUpdate: { countryRegions: newVal },
                };
              },
            })}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Status"
            selectedValues={currentStatus}
            setSelectedValues={createSetSelectedValues(
              LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_STATUS,
              setCurrentStatus,
              () => ({}),
              { setSearchActive: false },
            )}
            options={getStatusOptions(ALL_STATUS_OPTIONS)}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={createOnChangeFn({
              storageKey: LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_STATUS,
              setState: setCurrentStatus,
              updateStateAndStorage: false,
              run: (value, unchecked) => {
                const newVal = updateArr(currentStatus, value, unchecked);
                const statusOptions = getStatusOptions(ALL_STATUS_OPTIONS);
                const newParam = (Array.isArray(newVal) ? newVal : []).map(
                  (v) =>
                    statusOptions.find((o) => o.value === v) ?? {
                      value: v,
                      label: String(v),
                      name: String(v),
                    },
                ) as MultiSelectControlledOption[];
                return {
                  newParam,
                  defaultObjectUpdate: { collectorStatuses: newVal },
                };
              },
            })}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            mt: "22px",
            alignItems: "center",
          }}
        >
          <Button
            sx={{
              width: "140px",
              height: "36px",
              borderRadius: "8px",
            }}
            disabled={!enableSearch}
            variant={searchActive ? "contained" : "outlined"}
            onClick={() => {
              resetPage();
              handleUpdateMissingDocuments({
                ...defaultObject.current,
                ...paginatedQuery,
                page: 0,
                size,
              });
            }}
          >
            Search
          </Button>
          <Button
            sx={{
              width: "140px",
              height: "36px",
              borderRadius: "8px",
            }}
            variant="outlined"
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
          <Box
            sx={{
              minWidth: "fit-content",
              height: "42px",
              padding: "3px",
              border: `1px solid ${COLORS.Mainblue}`,
              borderRadius: "30px",
            }}
          >
            <Button
              variant={isOnlyMissingButtonActive ? "contained" : "text"}
              sx={{ borderRadius: "30px" }}
              onClick={onOnlyMissingButtonClick}
            >
              Only Missing
            </Button>
            <Button
              variant={!isOnlyMissingButtonActive ? "contained" : "text"}
              sx={{ borderRadius: "30px" }}
              onClick={onShowAllButtonClick}
            >
              Show all
            </Button>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "32px",
          flexWrap: "wrap",
          marginTop: "24px",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {dataBoxes.map(({ color, title }) => (
          <DataBoxes key={`${color}-${title}`} color={color} title={title} />
        ))}
      </Box>
      <Box
        sx={{
          position: "relative",
          width: "calc(100vw - 151px)",
          marginTop: "16px",
          "& table": {
            "& > thead": {
              position: "sticky",
              top: 0,
              zIndex: 2,
              "& > tr > th:nth-of-type(6)": {
                borderRight: "1px solid #054254",
              },
              "& > tr > th:nth-of-type(7)": {
                borderRight: "1px solid #054254",
              },
            },
            "& > tbody": {
              "& > tr > td:nth-of-type(6)": {
                borderRight: "1px solid #054254",
              },
              "& > tr > td:nth-of-type(7)": {
                borderRight: "1px solid #054254",
              },
            },
          },
        }}
      >
        {loadingMissingDocuments || isTableRequestInProgress ? (
          <Box
            sx={{
              "& > div: first-of-type": {
                marginTop: "100px",
                height: "auto",
              },
            }}
          >
            <Loading />
          </Box>
        ) : (
          <>
            {/* Row Selection Toolbar Component */}
            {totalSelectedRows > 0 && (
              <BulkActionsToolbar
                totalSelectedRows={totalSelectedRows}
                totalRows={totalRows}
                bulkActionsButtons={
                  <BulkActionsButtons
                    selectedCollectorIds={
                      allRowsSelected
                        ? allCurrentCollectorsIds
                        : Object.keys(selectedRows)
                    }
                    onRefreshAfterSave={handleUpdateMissingDocuments}
                  />
                }
              />
            )}
            {/* Table Component */}
            <MaterialReactTable
              muiTableContainerProps={{
                sx: {
                  border: "1px solid #C0D0D4",
                  borderRadius: "7px",
                  height: "calc(100vh - 410px)",
                  overflowY: "auto",
                  "& thead tr:first-of-type th:first-of-type": {
                    maxHeight: "47px",
                    padding: "5px 0px 0px 5px",
                  },
                  "& tbody tr": {
                    "& td:first-of-type": {
                      minHeight: "46px",
                      paddingLeft: "5px",
                      mt: "auto",
                    },
                  },
                },
              }}
              muiTablePaperProps={{
                sx: {
                  borderRadius: "10px",
                  boxShadow: "none",
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  height: "1px",
                  minHeight: "1px",
                  backgroundColor: "transparent",
                  "& .MuiIconButton-root": {
                    display: "none",
                  },
                },
              }}
              muiBottomToolbarProps={{
                sx: {
                  display: "none",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  borderRight: "1px solid #C0D0D4",
                  color: "#054254",
                  "& .MuiBadge-root": {
                    display: "none",
                  },
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  maxHeight: "60px",
                  borderRight: "1px solid #C0D0D4",
                  backgroundColor: "#fff",
                  padding: 0,
                  "& > div": {
                    maxHeight: "60px",
                  },
                },
              }}
              key={companyMonths as any}
              columns={MISSING_DOCUMENTS_COLUMNS}
              data={currentMissingData}
              onColumnFiltersChange={setColumnFilters}
              onGlobalFilterChange={setGlobalFilter}
              enableGlobalFilter={false}
              enablePagination
              manualPagination
              rowCount={totalRows}
              enableColumnResizing
              enablePinning
              enableStickyHeader
              muiTablePaginationProps={{
                rowsPerPageOptions: [50, 100, 200],
                showFirstButton: true,
                showLastButton: true,
              }}
              state={{ columnFilters, globalFilter }}
              getRowId={(row: any) => row.id}
              initialState={{
                pagination: { pageSize: size, pageIndex: page },
                columnPinning: {
                  left: [
                    "custom-row-selection-column",
                    "collector.siteName",
                    "collector.providerName",
                    "collector.accountNumber",
                    "collector.meterNumber",
                    "status",
                    "collector.utilityTypeName",
                  ],
                },
              }}
            />
          </>
        )}
      </Box>
      {!loadingMissingDocuments && !isTableRequestInProgress && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "10px",
          }}
        >
          <Pagination
            page={uiPage}
            rowsCount={totalRows}
            rowsPerPage={size}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleSizeChange}
          />
        </Box>
      )}
      {openMonthView && (
        <div>
          <Dialog
            open
            sx={{
              "& > div > div": {
                width: "1480px",
                minWidth: "1480px",
                maxWidth: "1480px",
                height: "fit-content",
                maxHeight: "90%",
                marginLeft: "130px",
                borderRadius: "8px",
                "& > div": {
                  minHeight: "100%",
                  "& > div": {
                    padding: "0",
                    height: "100%",
                  },
                },
              },
            }}
          >
            <Box>
              <CollectorMonthView
                year={selectedMonthCell.year}
                month={(
                  allMonths.indexOf(selectedMonthCell.month) + 1
                ).toString()}
                collectorMissingDocuments={collectorMissingDocuments!}
                onClose={() => setOpenMonthView(false)}
              />
            </Box>
          </Dialog>
        </div>
      )}
    </Box>
  );
};
